import { useState } from "react";
import type { InventoryItem } from "../inventaire/inventaireTypes";
import type { RecipeCostAnalysis } from "./configurationTypes";
import { CheckCircle2, Edit, Package, DollarSign, TrendingUp, ChefHat, ChevronDown, ChevronUp } from "lucide-react";

interface ConfiguredRecipeListProps {
  recipes: InventoryItem[];
  analysisMap: Map<string, RecipeCostAnalysis>;
  onEdit: (recipe: InventoryItem) => void;
}

const PREVIEW_COUNT = 8;

export function ConfiguredRecipeList({
  recipes,
  analysisMap,
  onEdit,
}: ConfiguredRecipeListProps) {
  const [expanded, setExpanded] = useState(false);
  const visibleRecipes = expanded ? recipes : recipes.slice(0, PREVIEW_COUNT);
  const hasMore = recipes.length > PREVIEW_COUNT;

  if (recipes.length === 0) {
    return (
      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-12 text-center">
        <Package className="w-16 h-16 text-[var(--muted-foreground)] opacity-30 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">
          Aucun article avec recette
        </h3>
        <p className="text-sm text-[var(--muted-foreground)] max-w-md mx-auto">
          Créez des recettes pour vos articles du menu dans la section ci-dessus.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-[var(--color-olive)]" />
          <h3 className="font-semibold text-[var(--foreground)]">
            Articles avec recette ({recipes.length})
          </h3>
          {hasMore && !expanded && (
            <span className="text-xs text-[var(--muted-foreground)]">
              · aperçu {visibleRecipes.length} sur {recipes.length}
            </span>
          )}
        </div>
      </div>

      {/* Grid layout with cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {visibleRecipes.map((recipe) => {
          const analysis = analysisMap.get(recipe.id);
          const ingredientCount = recipe.composition?.length || 0;

          return (
            <button
              key={recipe.id}
              onClick={() => onEdit(recipe)}
              className="bg-[var(--card)] border border-[var(--border)] rounded-lg overflow-hidden hover:border-[var(--color-steel)] hover:shadow-lg transition-all group text-left"
            >
              {/* Image thumbnail */}
              <div className="relative w-full aspect-video bg-[var(--muted)] overflow-hidden">
                {recipe.image ? (
                  <img
                    src={recipe.image}
                    alt={recipe.nom}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ChefHat className="w-12 h-12 text-[var(--muted-foreground)] opacity-30" />
                  </div>
                )}
                
                {/* Edit overlay on hover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex items-center gap-2 text-white font-medium">
                    <Edit className="w-4 h-4" />
                    Modifier
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {/* Title and category */}
                <div>
                  <h4 className="font-medium text-[var(--foreground)] truncate group-hover:text-[var(--color-steel)] transition-colors">
                    {recipe.nom}
                  </h4>
                  <span className="text-xs text-[var(--muted-foreground)]">
                    {recipe.categorie}
                  </span>
                </div>

                {/* Ingredient count */}
                <div className="flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
                  <Package className="w-3.5 h-3.5" />
                  {ingredientCount} ingrédient{ingredientCount > 1 ? "s" : ""}
                </div>

                {/* Cost and margin */}
                {analysis && (
                  <div className="space-y-1.5 pt-2 border-t border-[var(--border)]">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--muted-foreground)] flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Coût
                      </span>
                      <span className="font-medium text-[var(--foreground)]">
                        {analysis.coutTotal.toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--muted-foreground)] flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Marge
                      </span>
                      <span
                        className={`font-medium ${
                          analysis.margePercent >= 50
                            ? "text-[var(--color-olive)]"
                            : analysis.margePercent >= 30
                            ? "text-[var(--color-amber)]"
                            : "text-[var(--color-brick)]"
                        }`}
                      >
                        {analysis.margePercent.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--color-steel)] hover:text-[var(--color-steel)] hover:bg-[var(--muted)] transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Réduire
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Voir tout ({recipes.length})
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}